import { Component, OnChanges, OnInit } from '@angular/core';
import { LatestRequest, ParseIsoDate } from 'agu_app_shell';
import {
    ResearchPeerReview,
    ResearchPersonProfile,
    ResearchProfileFunding,
    ResearchProfileSession,
} from '../../models/research.models';
import { ResearchApiService } from '../../services/research-api.service';
import { PortalConfigService } from '../../services/portal-config.service';
import { PersonRoleLine } from '../people-finder/person-card.component';
import { DescribeError } from '../shared/describe-error';
import { LoadFailures, SupportMailto } from '../shared/load-failures';
import { PageErrorCopy, StatePanelCopy } from '../shared/state-copy';
import { RecentWorksSubtitle } from './recent-works-subtitle';

/** The one `LatestRequest` key: this component loads the member's own record and nothing else. */
const SELF_KEY = 'self';

/** One `What's shared` checklist row — verbatim from `My_profile.txt` / the "My profile" screen in `agu-member-portal-sep-1.html`. Static for every member (no wire field backs it), so a module constant rather than server data. */
export interface ProfileShareRow {
    label: string;
    shared: boolean;
}

/** The "what's shared" checklist on a profile: what other members can see and what stays private. The same for every member. */
export const PROFILE_SHARE_ROWS: readonly ProfileShareRow[] = [
    { label: 'Name, role and institution', shared: true },
    { label: 'City and country', shared: true },
    { label: 'Works, abstracts and ORCID', shared: true },
    { label: 'Co-author network and mutual connections', shared: true },
    { label: 'Email address', shared: false },
    { label: 'Award amounts', shared: false },
    { label: 'Saved items and searches', shared: false },
];

/** One `recentWorks` row with its venue/date meta line pre-formatted once at load. */
export interface ProfileWorkRow {
    title: string;
    url: string | null;
    metaLine: string | null;
}

/**
 * "Jan 2026" (month + year known) / "2026" (year only) / null (absent).
 * `ResearchProfileWork.date` arrives as "YYYY", "YYYY-MM" or "YYYY-MM-DD"
 * (apps/MJAPI/src/resolvers/research/memberProfile.ts `orcidWorkDate`/
 * `dateOnly`) — never a full ISO datetime — so a bare 4-digit year is
 * returned as-is rather than round-tripped through `Date` (which would
 * anchor it to Jan 1 and imply a month that was never stated).
 */
export function FormatWorkDateLabel(date: string | null): string | null {
    if (!date) return null;
    if (/^\d{4}$/.test(date)) return date;
    const parsed = ParseIsoDate(date);
    return parsed ? parsed.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) : null;
}

/** "Venue · Date" / "Venue" / "Date" / null when both are missing. */
export function FormatWorkMetaLine(venue: string | null, dateLabel: string | null): string | null {
    const parts = [venue, dateLabel].filter((v): v is string => Boolean(v));
    return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * "Research Scientist · NOAA · Atmospheric Sciences Division" — the role line
 * with the department appended, or the role line alone when no department is
 * stated. `roleLine` already reads "Not stated" when neither job title nor
 * organization is known, and a department is never appended to that.
 */
export function FormatAffiliationLine(roleLine: string, department: string | null): string {
    return department && roleLine !== 'Not stated' ? `${roleLine} · ${department}` : roleLine;
}

/** "31 co-authors" / "1 co-author". */
export function FormatCoauthorChip(coauthors: number): string {
    return `${coauthors} co-author${coauthors === 1 ? '' : 's'}`;
}

/**
 * One `Current awards` row, in the mockup's order:
 * "NSF, Cascade Volcano Observatory Network (Co-PI)" — funder, comma, title,
 * role in parens (parity/my-profile.md §4.4). The live view used to emit the
 * operands the other way round ("title (role), funder"). A null funder drops
 * the comma with it, and a null role drops the parens, rather than leaving a
 * stray separator; the mockup's leading award NUMBER has no field behind it
 * (`ResearchProfileFunding` carries only endYear/funder/role/startYear/title)
 * and is not invented.
 */
export function FormatFundingLine(funding: ResearchProfileFunding): string {
    const titled = funding.role ? `${funding.title} (${funding.role})` : funding.title;
    return funding.funder ? `${funding.funder}, ${titled}` : titled;
}

/** "AGU25 NH21B · Multi-hazard early warning" / "Multi-hazard early warning" when no meeting is stated. */
export function FormatSessionLine(session: ResearchProfileSession): string {
    return session.meeting ? `${session.meeting} · ${session.title}` : session.title;
}

/**
 * "JGR Atmospheres, GMD, WRR · 14 reviews" / "14 reviews" when no venues are
 * stated — the mockup's order, venues first (parity/my-profile.md §4.4); the
 * live view had the count first. The mockup's trailing "since 2023" has no
 * source field and is not invented. Never renders amounts — `ResearchPeerReview`
 * doesn't carry one.
 */
export function FormatPeerReviewLine(peerReview: ResearchPeerReview): string {
    const countPart = `${peerReview.count} review${peerReview.count === 1 ? '' : 's'}`;
    return peerReview.venues.length > 0 ? `${peerReview.venues.join(', ')} · ${countPart}` : countPart;
}

/** What a load renders; computed once per load, off the zoneless render path. */
interface ProfileView {
    readonly profile: ResearchPersonProfile | null;
    readonly workRows: ProfileWorkRow[];
    readonly recentWorksSubtitle: string;
}

const EMPTY_VIEW: ProfileView = { profile: null, workRows: [], recentWorksSubtitle: '' };

/**
 * The member's OWN profile page (`MyProfilePageComponent`, own record via
 * `researchMyProfile`). Until the 2026-10-24 checklist round this component also
 * rendered another member's record for Collaborator Finder (`IsSelf=false`);
 * that view is now `ra-person-detail` on the detail-view chrome, so the
 * Save/Pin/"Not for me" actions, the back pill and the copy split are gone
 * from here. The header is the mockup's plain `agu-plain-header` (Task 7 /
 * parity/my-profile.md §4.1), which has no icon chip and no band.
 *
 * `:host { display: contents }` (see the .scss) makes the wrapper element
 * this component renders on invisible to the flex layout `mj-page-layout`
 * expects — the same technique `AGUAppShell`'s `app-sidebar.component.scss`
 * uses — so `MyProfilePageComponent` can place `<ra-person-profile>` directly
 * inside `<mj-page-layout>` and still get `mj-page-body` as a true flex sibling
 * rather than nested inside an inert wrapper box. Custom properties still
 * inherit through a `display: contents` element, so the page host's
 * `data-accent` reaches this component's rules unchanged.
 */
@Component({
    standalone: false,
    selector: 'ra-person-profile',
    templateUrl: './ra-person-profile.component.html',
    styleUrl: './ra-person-profile.component.scss',
})
/**
 * The member's own profile page: loads the own record once. Product states (PD §5): 3 skeleton cards while loading
 * (P/proto-states.js:130) and the error panel when the load fails (state-copy.ts `PageErrorCopy`). The prototype's save-failure
 * banner (:123) has no counterpart: this page edits nothing (its fields are managed on agu.org).
 */
export class RaPersonProfileComponent implements OnInit, OnChanges {
    readonly ShareRows = PROFILE_SHARE_ROWS;

    profile: ResearchPersonProfile | null = null;
    loading = false;
    error: string | null = null;
    /** The error panel's foot, retry note and Contact support link (PD §5). */
    readonly failures = new LoadFailures();
    readonly ErrorCopy: StatePanelCopy = PageErrorCopy('your profile');

    accountSettingsUrl: string | null = null;

    /** Computed once per `Load()`, never in a getter — see `FormatWorkDateLabel`'s doc comment / ui-best-practices §7. */
    workRows: ProfileWorkRow[] = [];
    recentWorksSubtitle = '';

    /** Latest-load-wins over Retry re-issues; the key never changes, so a mount loads exactly once. */
    private readonly request = new LatestRequest<string>();

    constructor(
        private readonly api: ResearchApiService,
        private readonly portalConfig: PortalConfigService
    ) {}

    ngOnInit(): void {
        void this.LoadConfig();
        this.LoadIfChanged();
    }

    ngOnChanges(): void {
        // No input drives a reload; `HasChanged` makes this a no-op after the first mount.
        this.LoadIfChanged();
    }

    private LoadIfChanged(): void {
        if (this.request.HasChanged(SELF_KEY)) {
            void this.Load();
        }
    }

    private async Load(key: string = SELF_KEY): Promise<void> {
        this.loading = true;
        this.error = null;
        this.profile = null;
        // Fetch AND map from the captured key inside the callback: a mapper throw settles `failed`,
        // and nothing below reads the live inputs.
        const outcome = await this.request.Run(key, () => this.FetchView());
        // A newer key owns the view (and its loading flag) now — drop this response.
        if (outcome.Status === 'stale') return;
        this.loading = false;
        if (outcome.Status === 'failed') {
            this.error = DescribeError(outcome.Error);
            this.failures.Record(outcome.Error);
            return;
        }
        this.failures.Reset();
        const view = outcome.Value;
        this.profile = view.profile;
        this.workRows = view.workRows;
        this.recentWorksSubtitle = view.recentWorksSubtitle;
    }

    /** Resolves and maps the member's own record. */
    private async FetchView(): Promise<ProfileView> {
        const profile = await this.api.MyProfile();
        if (!profile) return EMPTY_VIEW;
        const workRows = profile.recentWorks.map((w) => ({
            title: w.title,
            url: w.url,
            metaLine: FormatWorkMetaLine(w.venue, FormatWorkDateLabel(w.date)),
        }));
        return {
            profile,
            workRows,
            // The resolver never mixes sources — recentWorks is ALL orcid or ALL
            // content works (memberProfile.ts falls back to content works only
            // when the ORCID list is empty) — so the first item's `kind` names
            // the source for the whole list.
            recentWorksSubtitle: RecentWorksSubtitle(workRows.length, profile.recentWorks[0]?.kind === 'orcid', true),
        };
    }

    private async LoadConfig(): Promise<void> {
        try {
            const config = await this.portalConfig.Load();
            this.accountSettingsUrl = config.accountSettingsUrl;
            this.failures.SupportHref = SupportMailto(config.supportEmail);
        } catch {
            // Non-fatal — both "Account settings ↗" links guard on
            // `accountSettingsUrl` being set; the profile itself still loads.
        }
    }

    Retry(): void {
        void this.Load();
    }

    get RoleLine(): string {
        return this.profile ? PersonRoleLine(this.profile) : 'Not stated';
    }

    /**
     * The mockup's `.prole` first line: role, organization and department on
     * ONE line, separated by ` · ` (parity/my-profile.md §4.3 / §6 — "three
     * fields joined with ` · `; drop separators when a part is null"). The
     * second line is `profile.location`, rendered by the template after a
     * `<br>`; it is omitted entirely when the member has no location rather
     * than leaving a blank line.
     */
    get AffiliationLine(): string {
        return this.profile ? FormatAffiliationLine(PersonRoleLine(this.profile), this.profile.department) : 'Not stated';
    }

    /** The page sub line (my-profile.html `.top` sub). */
    get HeaderSub(): string {
        return 'Exactly what other members see when you appear in People Finder';
    }

    /** Whether the identity card's link rail has anything in it — the whole rail (and its hairline) is dropped when it would be empty. */
    get HasLinks(): boolean {
        return !!this.profile && (!!this.profile.orcid || !!this.profile.linkedInUrl || !!this.profile.aguProfileUrl);
    }

    get CoauthorChip(): string {
        return this.profile ? FormatCoauthorChip(this.profile.stats.coauthors) : '';
    }

    /** Whether the "Funding & service" card has anything to show — hidden entirely when every sub-block is empty. */
    get HasFundingService(): boolean {
        if (!this.profile) return false;
        return this.profile.funding.length > 0 || this.profile.sessionsConvened.length > 0 || !!this.profile.peerReview || this.profile.membershipSince !== null;
    }

    FundingLine(funding: ResearchProfileFunding): string {
        return FormatFundingLine(funding);
    }

    SessionLine(session: ResearchProfileSession): string {
        return FormatSessionLine(session);
    }

    PeerReviewLine(peerReview: ResearchPeerReview): string {
        return FormatPeerReviewLine(peerReview);
    }
}
